/**
 * TypeScript types for Reports & Analytics module
 */

// Report types enum
export enum ReportType {
  DAILY_REPLENISHMENT_PLAN = 'daily_replenishment_plan',
  STOCK_STATUS = 'stock_status',
  ORDER_TRACKING = 'order_tracking',
  STOCKOUT_ANALYSIS = 'stockout_analysis',
  EXCESS_INVENTORY = 'excess_inventory',
  EXPIRING_PRODUCTS = 'expiring_products',
}

// Report request interface
export interface ReportRequest {
  report_type: ReportType;
  start_date?: string;
  end_date?: string;
  store_id?: string;
  category?: string;
  status?: string;
  threshold?: number;
}

// Report metadata
export interface ReportMetadata {
  report_type: string;
  generated_at: string;
  parameters: Record<string, any>;
  record_count: number;
}

// Report response wrapper
export interface ReportResponse<T = any> {
  metadata: ReportMetadata;
  data: T[];
  start_date?: string;
  end_date?: string;
  store_ids?: string[];
  product_ids?: string[];
}

// ========================================================================
// DAILY REPLENISHMENT PLAN
// ========================================================================

export interface DailyReplenishmentPlanItem {
  order_id: string;
  store_name: string;
  product_name: string;
  sku: string;
  current_stock: number;
  in_transit: number;
  quantity_ordered: number;
  expected_delivery: string | null;
  status: string;
  priority: number;
}

// ========================================================================
// STOCK STATUS REPORT
// ========================================================================

export enum StockStatus {
  STOCKOUT = 'stockout',
  CRITICAL = 'critical',
  MEDIUM_RISK = 'medium_risk',
  HEALTHY = 'healthy',
  OVERSTOCK = 'overstock',
  HIGH_OVERSTOCK = 'high_overstock',
}

export interface StockStatusItem {
  store_name: string;
  product_name: string;
  sku: string;
  brand?: string;
  category: string;
  lifecycle_stage?: string | null;
  item_status: string;
  current_stock: number;
  in_transit: number;
  min_stock: number | null;
  max_stock: number | null;
  target_stock?: number;
  target_coverage_days?: number;
  avg_daily_sales: number;
  days_of_supply: number;
  months_of_cover?: number;
  status: StockStatus | string;
  status_priority: number;
  is_high_overstock?: boolean;
  suggested_action?: string;
  stock_coverage: string;
}

// ========================================================================
// ORDER TRACKING REPORT
// ========================================================================

export enum OrderStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  TRANSMITTED = 'transmitted',
  ACKNOWLEDGED = 'acknowledged',
  DELIVERED = 'delivered',
  CANCELLED = 'cancelled',
}

export enum DeliveryStatus {
  ON_TIME = 'on_time',
  EARLY = 'early',
  LATE = 'late',
  OVERDUE = 'overdue',
}

export interface OrderTrackingItem {
  order_id: string;
  order_date: string;
  store_name: string;
  product_name: string;
  sku: string;
  quantity_ordered: number;
  status: OrderStatus | string;
  expected_delivery: string | null;
  order_age_days: number;
  delivery_status: DeliveryStatus | string;
  supplier: string;
}

// ========================================================================
// STOCKOUT ANALYSIS REPORT
// ========================================================================

export enum StockoutSeverity {
  CRITICAL = 'critical',
  MODERATE = 'moderate',
  LOW = 'low',
}

export interface StockoutAnalysisItem {
  store_name: string;
  product_name: string;
  sku: string;
  category: string;
  current_stock: number;
  in_transit: number;
  avg_daily_sales: number;
  days_out_of_stock: number;
  estimated_lost_sales_units: number;
  stockout_severity: StockoutSeverity | string;
  recommended_action: string;
  status: string;
}

// ========================================================================
// EXCESS INVENTORY REPORT
// ========================================================================

export interface ExcessInventoryItem {
  store_name: string;
  product_name: string;
  sku: string;
  category: string;
  current_stock: number;
  max_stock: number | null;
  avg_daily_sales: number;
  days_of_supply: number;
  excess_quantity: number;
  excess_value_estimate: number;
  recommendation: string;
  slow_moving: boolean;
}

// ========================================================================
// EXPIRING PRODUCTS REPORT
// ========================================================================

export interface ExpiringProductItem {
  store_name: string;
  product_name: string;
  sku: string;
  category: string;
  batch_number: string;
  current_stock: number;
  expiry_date: string;
  days_until_expiry: number;
  urgency: 'critical' | 'high' | 'medium' | 'low';
  recommended_action: string;
}

// ========================================================================
// REPORT FILTERS
// ========================================================================

export interface ReportFilters {
  startDate?: Date | null;
  endDate?: Date | null;
  storeId?: string;
  category?: string;
  status?: string;
  threshold?: number;
}

// ========================================================================
// EXPORT OPTIONS
// ========================================================================

export enum ExportFormat {
  CSV = 'csv',
  JSON = 'json',
  EXCEL = 'excel',
}

export interface ExportOptions {
  format: ExportFormat;
  filename?: string;
}

// ========================================================================
// ANALYTICAL REPORTS
// ========================================================================

export interface SalesTrendItem {
  date: string;
  product_name: string;
  sku: string;
  category: string;
  store_name: string;
  quantity_sold: number;
  revenue: number;
  trend: string;
}

export interface SupplierPerformanceItem {
  supplier_name: string;
  total_orders: number;
  on_time_deliveries: number;
  late_deliveries: number;
  on_time_delivery_rate: number;
  average_lead_time_days: number;
  total_quantity_ordered: number;
  performance_score: number;
}

export interface StorePerformanceItem {
  store_id: string;
  store_name: string;
  store_type: string;
  total_units_sold: number;
  total_revenue: number;
  stockout_count: number;
  inventory_value: number;
  fill_rate: number;
}

export interface CategoryPerformanceItem {
  category: string;
  product_count: number;
  total_units_sold: number;
  total_revenue: number;
  avg_transaction_value: number;
  revenue_share_percent: number;
  inventory_turnover: number;
}

export interface InventoryTurnoverItem {
  product_name: string;
  sku: string;
  category: string;
  store_name: string;
  current_stock: number;
  units_sold: number;
  turnover_ratio: number;
  days_to_sell: number;
  turnover_category: string;
  status?: string;
}

export interface ServiceLevelItem {
  store_name: string;
  store_type: string;
  fill_rate: number;
  stockout_incidents: number;
  perfect_order_rate: number;
  total_orders: number;
  on_time_deliveries: number;
  service_level_score: number;
}
